import React, { useContext, useEffect, useState } from "react";
import { isDragContext } from "../../../context/IsDragContext";
import { SignTypeContext } from "../../../context/SignTypeContext";
import {
  textReactHeight,
  signReactWidth,
  signReactHeight,
  documentHeight,
} from "../../../utils/measuresForResponsiveness";
import { ContainerRect, Handle, HandleFixed, Input, Svg } from "./styled";

interface ResizableProps {
  id: string;
  type: string;
  email: string;
  x: string;
  y: string;
  yRelative?: string;
  pageNumber?: number;
  color: string;
  linkSvg?: string;
  selectName?: string;
  onDelete: () => void;
}

export const Resizable: React.FC<ResizableProps> = ({
  id,
  type,
  email,
  x,
  y,
  yRelative,
  pageNumber,
  color,
  linkSvg,
  selectName,
  onDelete,
}) => {
  const [isResizing, setIsResizing] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffsetX, setDragOffsetX] = useState(0);
  const [dragOffsetY, setDragOffsetY] = useState(0);
  const [handle, setHandle] = useState("");
  const [startX, setStartX] = useState(0);
  const [startY, setStartY] = useState(0);
  const [rectX, setRectX] = useState(0);
  const [rectY, setRectY] = useState(0);
  const [rectYRelative, setRectYRelative] = useState(yRelative);
  const [rectWidth, setRectWidth] = useState(textReactHeight);
  const [rectHeight, setRectHeight] = useState(textReactHeight);
  const [inputValue, setInputValue] = useState("");
  const [showImage, setShowImage] = useState(false);
  const [page, setPage] = useState(pageNumber);
  const imageX = rectX - (rectWidth - rectHeight) / 2;
  const imageY = rectY + rectHeight - rectHeight * 0.8;

  const { currentInputError } = useContext(SignTypeContext);
  const { setIsDrag } = useContext(isDragContext);

  useEffect(() => {
    setRectX(Number(x.replace("px", "")));
    setRectY(Number(y.replace("px", "")));

    if (isVerificationType) {
      setRectWidth(signReactWidth);
      setRectHeight(signReactHeight);
    }
    if (type === "text" || type === "select") {
      setRectWidth(signReactWidth);
      setRectHeight(textReactHeight);
    }
    if (type === "checkbox") {
      setRectWidth(25);
      setRectHeight(25);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const minRectWidth = 20;
  const minRectHeight = 20;
  const maxRectWidth = 800;
  const maxRectHeight = 600;

  const isVerificationType = type === "signature" || type === "cpf" || type === "cnpj" || type === "date";

  const handleMouseDown = (
    event: React.MouseEvent<SVGElement>,
    selectedHandle: string
  ) => {
    event.stopPropagation();

    setIsResizing(true);
    setStartX(event.clientX);
    setStartY(event.clientY);
    setHandle(selectedHandle);
  };
  const handleTouchStart = (
    event: React.TouchEvent<SVGElement>,
    selectedHandle: string
  ) => {
    event.stopPropagation();
    setIsResizing(true);
    setStartX(event.touches[0].clientX);
    setStartY(event.touches[0].clientY);
    setHandle(selectedHandle);
  };

  const handleTouchMove = (event: React.TouchEvent<SVGElement>) => {
    if (isResizing) {
      setIsDrag(true);
      handleResize(event.touches[0] as Touch);
    } else if (isDragging) {
      setIsDrag(true);
      handleDrag(event.touches[0] as Touch);
    } else {
      setIsDrag(false);
    }
  };

  const handleTouchEnd = () => {
    setIsDrag(false);
    handleMouseUp();
  };

  const handleMouseUp = () => {
    const pageHeight = documentHeight;
    const gapHeight = 4;
    const pageNumber = Math.floor(rectY / (pageHeight + gapHeight)) + 1;
    const heightPage =
      pageNumber > 1 ? (pageNumber - 1) * (pageHeight + gapHeight) : 0;
    const yRelativePosition = rectY - heightPage;

    setPage(pageNumber);

    setRectYRelative(yRelativePosition.toString());
    setIsResizing(false);
    setIsDragging(false);
  };
  
  const handleMouseMove = (event: React.MouseEvent<SVGElement>) => {
    if (isResizing) {
      handleResize(event);
    } else if (isDragging) {
      handleDrag(event);
    }
  };

  const handleResize = (event: React.MouseEvent<SVGElement> | Touch) => {
    if ("stopPropagation" in event) {
      event.stopPropagation();
    }
    const widthChange = event.clientX - startX;
    const heightChange = event.clientY - startY;

    let newRectWidth = rectWidth;
    let newRectHeight = rectHeight;
    let newRectX = rectX;
    let newRectY = rectY;

    if (handle === "top-left") {
      newRectWidth -= widthChange;
      newRectHeight -= heightChange;
      newRectX += widthChange;
      newRectY += heightChange;
    } else if (handle === "top-right") {
      newRectWidth += widthChange;
      newRectHeight -= heightChange;
      newRectY += heightChange;
    } else if (handle === "bottom-left") {
      newRectWidth -= widthChange;
      newRectHeight += heightChange;
      newRectX += widthChange;
    } else if (handle === "bottom-right") {
      newRectWidth += widthChange;
      newRectHeight += heightChange;
    }

    if (isVerificationType) {
      const aspectRatio = signReactWidth / textReactHeight;
      const newAspectRatio = newRectWidth / newRectHeight;
      if (newAspectRatio > aspectRatio) {
        newRectWidth = newRectHeight * aspectRatio;
      } else {
        newRectHeight = newRectWidth / aspectRatio;
      }
    } else {
      let newRectX = rectX;
      let newRectY = rectY;

      if (handle.includes("left")) {
        newRectX += widthChange / 30;
        newRectWidth -= widthChange / 30;
      } else if (handle.includes("right")) {
        newRectWidth += widthChange / 30;
      }

      if (handle.includes("top")) {
        newRectY += heightChange / 30;
        newRectHeight -= heightChange / 30;
      } else if (handle.includes("bottom")) {
        newRectHeight += heightChange / 30;
      }

      newRectWidth = Math.max(
        minRectWidth,
        Math.min(newRectWidth, maxRectWidth)
      );
      newRectHeight = Math.max(
        minRectHeight,
        Math.min(newRectHeight, maxRectHeight)
      );

      setRectWidth(newRectWidth);
      setRectHeight(newRectHeight);
      setRectX(newRectX);
      setRectY(newRectY);
    }

    newRectWidth = Math.max(minRectWidth, Math.min(newRectWidth, maxRectWidth));
    newRectHeight = Math.max(
      minRectHeight,
      Math.min(newRectHeight, maxRectHeight)
    );

    setRectWidth(newRectWidth);
    setRectHeight(newRectHeight);
    setRectX(newRectX);
    setRectY(newRectY);

    setStartX(event.clientX);
    setStartY(event.clientY);
  };

  const handleDragStart = (event: React.MouseEvent<SVGElement>) => {
    event.stopPropagation();
    setIsDragging(true);
    setDragOffsetX(event.clientX - rectX);
    setDragOffsetY(event.clientY - rectY);
  };

  const handleDrag = (event: React.MouseEvent<SVGElement> | Touch) => {
    if ("stopPropagation" in event) {
      event.stopPropagation();
    }
    const newRectX = event.clientX - dragOffsetX;
    const newRectY = event.clientY - dragOffsetY;

    if (
      currentInputError.split("rect-")[1] === id &&
      currentInputError.length
    ) {
      const currentInput = document.getElementById(currentInputError);
      if (currentInput) {
        currentInput.style.fill = color;
      }
    }

    setRectX(newRectX);
    setRectY(newRectY);
  };

  const handleMouseClick = (event: React.MouseEvent<SVGElement>) => {
    event.stopPropagation();
  };

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(event.target.value);
  };

  const handleDoubleClick = () => {
    setShowImage(!showImage);
  };
  const handleTouchDragStart = (event: React.TouchEvent<SVGElement>) => {
    event.stopPropagation();
    setIsDragging(true);
    setDragOffsetX(event.touches[0].clientX - rectX);
    setDragOffsetY(event.touches[0].clientY - rectY);
  };
  const signsWithImage = [
    "initial",
    "signature",
    "text",
    "checkbox",
    "cpf",
    "cnpj",
    "date",
    "select",
  ];
  
  return (
    <>
      <Svg
        onMouseMove={handleMouseMove}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        onTouchStart={handleTouchDragStart}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
        onClickCapture={handleMouseClick}
        id={id}
      >
        {type !== "signature" && (
          <>
            <Handle
              isClicked={isResizing}
              x={isResizing ? 0 : rectX + rectWidth - 5}
              y={isResizing ? 0 : rectY - 5}
              cursor="all-scroll"
              onMouseDown={(e: React.MouseEvent<SVGElement>) =>
                handleMouseDown(e, "top-right")
              }
              onTouchStart={(e: any) => handleTouchStart(e, "top-right")}
            />
            <Handle
              isClicked={isResizing}
              x={isResizing ? 0 : rectX - 5}
              y={isResizing ? 0 : rectY - 5}
              cursor="all-scroll"
              onMouseDown={(e: React.MouseEvent<SVGElement>) =>
                handleMouseDown(e, "top-left")
              }
              onTouchStart={(e: any) => handleTouchStart(e, "top-left")}
            />
            <Handle
              isClicked={isResizing}
              x={isResizing ? 0 : rectX - 5}
              y={isResizing ? 0 : rectY + rectHeight - 5}
              cursor="all-scroll"
              onMouseDown={(e: React.MouseEvent<SVGElement>) =>
                handleMouseDown(e, "bottom-left")
              }
              onTouchStart={(e: any) => handleTouchStart(e, "bottom-left")}
            />
          </>
        )}
        <Handle
          isClicked={isResizing}
          x={isResizing ? 0 : rectX + rectWidth - 5}
          y={isResizing ? 0 : rectY + rectHeight - 5}
          cursor="all-scroll"
          onMouseDown={(e: React.MouseEvent<SVGElement>) =>
            handleMouseDown(e, "bottom-right")
          }
          onTouchStart={(e: any) => handleTouchStart(e, "bottom-right")}
        />

        <HandleFixed isSignature={type} x={rectX - 5} y={rectY - 5} />
        <HandleFixed
          isSignature={type}
          x={rectX + rectWidth - 5}
          y={rectY - 5}
        />
        <HandleFixed
          isSignature={type}
          x={rectX - 5}
          y={rectY + rectHeight - 5}
        />
        <HandleFixed x={rectX + rectWidth - 5} y={rectY + rectHeight - 5} />
        <rect
          id={`rect-${id}`}
          x={rectX}
          y={rectY}
          y1={rectYRelative}
          width={rectWidth}
          height={rectHeight}
          pathLength={page}
          fill={color}
          type={type}
          rx={8}
          opacity={0.5}
          stroke="#2c2c2c"
          strokeWidth={1}
          onMouseDown={handleDragStart}
          style={{ cursor: isDragging ? "grabbing" : "grab" }}
          className={email}
          values={
            inputValue && type === "select"
              ? JSON.stringify(inputValue.split(",").map((item) => item.trim()))
              : inputValue
              ? inputValue
              : `${selectName}`
          }
          name={selectName}
          onDoubleClick={handleDoubleClick}
        />
        {showImage && (
          <ContainerRect>
            <rect
              x={rectX + rectWidth + 5}
              y={rectY + rectHeight + 5}
              width={30}
              height={30}
              rx={99}
              onMouseDown={() => onDelete()}
              style={{ cursor: "pointer" }}
            />
            <image
              xlinkHref="x.svg"
              x={rectX + rectWidth + 7}
              y={rectY + rectHeight + 7}
              width={25}
              height={25}
              style={{ pointerEvents: "none" }}
            />
          </ContainerRect>
        )}
        {signsWithImage.includes(type) && (
          <>
            <image
              xlinkHref={linkSvg}
              x={isVerificationType ? imageX : rectX}
              y={isVerificationType ? imageY : rectY}
              width={rectWidth}
              height={rectHeight}
              style={{ pointerEvents: "none" }}
            />
            <foreignObject
              x={rectX + 7}
              y={rectY - 13}
              width={rectWidth - 13}
              height={12}
              style={{ zIndex: -100 }}
            >
              <p
                style={{
                  fontSize: "10px",
                  color: "#000000",
                  fontWeight: "bold",
                }}
              >
                {`ID ${id.replace("sign-Element-", "")}`}
              </p>
            </foreignObject>
          </>
        )}

        {(type === "text" || type === "select") && (
          <foreignObject
            x={rectX + 5}
            y={rectY + 5}
            width={rectWidth - 10}
            height={rectHeight - 10}
            style={{ zIndex: -100 }}
          >
            <Input
              value={inputValue}
              onChange={handleInputChange}
              placeholder={
                type === "text"
                  ? "Digite o texto desejado..."
                  : "Digite as opções separadas por vírgula..."
              }
            />
          </foreignObject>
        )}
        {(type === "cpf" || type === "cnpj" || type === "date") && (
            <text
              x={rectX + rectWidth / 2}
              y={rectY + rectHeight / 2}
              textAnchor="middle"
              alignmentBaseline="middle"
              fontSize="12"
              fontWeight={700}
              fill="black"
              style={{ pointerEvents: "none" }}
            >
              {type === "cpf" ? "CPF" : type === "cnpj" ? "CNPJ" : "DATA"}
            </text>
        )}
      </Svg>
    </>
  );
};
